import React from "react";
import { Sparkles, Link as LinkIcon } from "lucide-react";
import { TAG_ICON, isEcosystem, heroGradient, firstTopicTag, initials, trackBadge } from "../lib/tagMeta";
import { topicImage } from "../lib/topicImages";

export function TrackBadge({ track, className = "" }) {
  const label = trackBadge(track);
  const isAI = track === "AI";
  const isWeb3 = track === "Web3";
  const both = track === "AI, Web3";
  const style = both
    ? "bg-violet-100 text-violet-600"
    : isAI
    ? "bg-violet-100 text-violet-600"
    : "bg-gold-50 text-gold-800";
  const Icon = isWeb3 ? LinkIcon : Sparkles;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-pill px-2.5 py-1 text-[12px] font-bold tracking-wide ${style} ${className}`}
      data-testid="track-badge"
    >
      <Icon size={13} /> {label}
    </span>
  );
}

export function StageChip({ stage, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-pill bg-white/80 px-2.5 py-1 text-[12px] font-bold text-navy-900 backdrop-blur ${className}`}>
      {stage === "Live" ? "Live 🚀" : stage}
    </span>
  );
}

export function TagChip({ tag, selected = false, onClick, size = "md" }) {
  const Icon = TAG_ICON[tag];
  const eco = isEcosystem(tag);
  const base = selected
    ? "bg-gold-500 text-navy-900 border-gold-500"
    : eco
    ? "bg-gold-50 text-gold-800 border-gold-500/40"
    : "bg-navy-100 text-navy-900 border-transparent";
  const pad = size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-[12px]";
  const Comp = onClick ? "button" : "span";
  return (
    <Comp
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-pill border font-bold transition-transform active:scale-95 ${base} ${pad}`}
      data-testid={`tag-chip-${tag}`}
    >
      {Icon && <Icon size={12} />} {tag}
    </Comp>
  );
}

// sparkle positions seeded by slug
function sparkles(slug) {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 33 + slug.charCodeAt(i)) >>> 0;
  const pts = [];
  for (let i = 0; i < 6; i++) {
    h = (h * 1103515245 + 12345) >>> 0;
    pts.push({ x: (h % 90) + 5, y: ((h >> 8) % 80) + 5, s: ((h >> 4) % 10) + 6 });
  }
  return pts;
}

export function HeroArt({ project, variant = "page", rounded = "rounded-t-card", className = "", showUbot = true }) {
  const card = variant === "card";
  const src = (card ? project.hero_card_url : null) || project.hero_image_url || topicImage(project.u_tags);
  const hasUbot = (project.u_tags || []).includes("#U-Bot");
  return (
    <div
      className={`relative overflow-hidden ${rounded} ${className}`}
      style={{ background: heroGradient(project.slug) }}
      data-testid="hero-art"
    >
      <img src={src} alt={project.project} loading="lazy" className="h-full w-full object-cover" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy-950/40 via-transparent to-navy-950/20" />
      {hasUbot && showUbot && (
        <img src="/assets/ubot.png" alt="" className="absolute bottom-1 right-1 h-14 w-14 object-contain drop-shadow-lg" />
      )}
    </div>
  );
}

export function ProgressPill({ seen, total }) {
  const pct = Math.min(100, Math.round((seen / total) * 100));
  return (
    <div className="flex items-center gap-2" data-testid="progress-pill">
      <div className="h-1.5 w-20 overflow-hidden rounded-pill bg-white/15">
        <div className="h-full rounded-pill bg-gold-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-num text-[12px] text-on-dark-muted">{seen} of {total}</span>
    </div>
  );
}

export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      className={`inline-flex h-[52px] items-center justify-center gap-2 rounded-pill bg-gold-500 px-6 font-display text-[18px] font-bold text-navy-900 shadow-soft transition-all hover:bg-gold-400 active:scale-[.97] disabled:opacity-40 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Dots() {
  return (
    <span className="inline-flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span key={i} className="h-2 w-2 rounded-full bg-violet-500 animate-dot-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
      ))}
    </span>
  );
}

export function Avatar({ name = "", src, size = 40, className = "" }) {
  const dim = { width: size, height: size };
  if (src) {
    return <img src={src} alt={name} style={dim} className={`rounded-full object-cover ${className}`} />;
  }
  return (
    <span style={dim} className={`inline-flex items-center justify-center rounded-full bg-navy-800 font-display font-bold text-gold-400 ${className}`}>
      <span style={{ fontSize: size * 0.4 }}>{initials(name)}</span>
    </span>
  );
}

export function AvatarStack({ members = [], size = 34, max = 5, ring = "ring-white" }) {
  if (!members.length) return null;
  const shown = members.slice(0, max);
  const extra = members.length - shown.length;
  return (
    <div className="flex items-center" data-testid="avatar-stack">
      {shown.map((m, i) => (
        <span key={i} style={{ marginLeft: i === 0 ? 0 : -size * 0.32, zIndex: shown.length - i }} className={`rounded-full ring-2 ${ring}`}>
          <Avatar name={m.name} src={m.photo_url} size={size} />
        </span>
      ))}
      {extra > 0 && (
        <span style={{ marginLeft: -size * 0.32, width: size, height: size, zIndex: 0 }}
          className={`inline-flex items-center justify-center rounded-full bg-navy-100 text-[11px] font-bold text-navy-900 ring-2 ${ring}`}>
          +{extra}
        </span>
      )}
    </div>
  );
}
